import { track } from '@/lib/analytics/track';
import { TrackingEventName } from '@/models/analytics/eventTypes';
import { type PatientSearchedAnalyticsProps } from '@heidi/patients/contracts';

/**
 * The app-side emit host for `patient_searched` (`@heidi/patients`), openspec
 * task 2.1b.
 *
 * Ruling 24: the package's search query decides WHEN the event fires — once per
 * BE-confirmed response, inside `queryFn`, so cache hits do not emit — and
 * measures the four facts it can honestly measure; the app delegate adds the
 * three fields only it can resolve (session, `search_id`, flag cohort). This
 * function adds the `event_name` and the routing.
 *
 * ROUTING IS PRESERVED, NOT TIDIED: the Rematch wrapper this replaces
 * (`analyticsScribe.trackPatientSearched`) passed `options: { amplitude: false }`,
 * so this does too. `trackV2` is a pure alias of `track`
 * (`src/lib/analytics/track-v2.ts`), so the same function object receives the
 * same payload — the only change is that the store is no longer on the path,
 * which is the point of the conversion (Wave 1 · P2 severs the dispatch bus).
 *
 * NO PII BY SCHEMA: the payload carries `query_length`, never the query. The v2
 * schema dropped `query` deliberately — clinicians type patient names into this
 * field — and the type in `@heidi/patients/contracts` has no `query` member, so
 * a call site cannot reintroduce one without failing to compile.
 *
 * THE PROPS TYPE IS NARROWER THAN THE APP UNION'S MEMBER, on purpose. The union
 * types `patient_searched` with `PatientSearchedEventProps` from
 * `src/models/analytics/features/integrations.ts`, which carries an
 * `integration_type?: IntegrationType` field no patients surface sets and whose
 * union is built from app-only integration modules. Every field there is
 * optional, so this shape is assignable to it; the reasoning for leaving that
 * type where it is sits in `patient-search-analytics.types.ts`.
 *
 * WHY THE ENUM MEMBER AND NOT `patientSearched.name`: `event_name` discriminates
 * `TrackingEventV2` on string ENUM members and a string literal does not compile
 * in one's place. The agreement is asserted in this module's suite instead.
 */
export const trackPatientSearched = (props: PatientSearchedAnalyticsProps): void => {
  track({
    options: { amplitude: false },
    event_name: TrackingEventName.PatientSearched,
    props,
  });
};
